import { ChangeDetectorRef, Component, Input, ElementRef, ViewChild, OnDestroy, HostListener } from '@angular/core';

import {
  computePosition,
  flip,
  shift,
  offset,
  arrow,
  Placement,
} from '@floating-ui/dom';

type TooltipPlacement = 'top' | 'right' | 'bottom' | 'left';
type TooltipVariant = 'default' | 'dark';

@Component({
  selector: 'app-tooltip',
  standalone: true,
  imports: [],
  templateUrl: './tootip.component.html',
  styles: [`
    :host {
      display: inline-flex;
    }
  `]
})
export class TooltipComponent implements OnDestroy {
  @Input() content: string = '';
  @Input() placement: TooltipPlacement = 'top';
  @Input() variant: TooltipVariant = 'default';

  @ViewChild('triggerRef') triggerRef!: ElementRef;
  @ViewChild('floatingRef') floatingRef!: ElementRef;
  @ViewChild('arrowRef') arrowRef!: ElementRef;

  isOpen = false;
  floatingStyles = { top: 0, left: 0 };
  arrowStyles: any = {};
  arrowBorderSides = '';

  private openTimeout?: any;

  private placementMap: Record<TooltipPlacement, Placement> = {
    top: 'top',
    right: 'right',
    bottom: 'bottom',
    left: 'left',
  };

  constructor(private cdr: ChangeDetectorRef) {}

  get variantClasses(): string {
    return this.variant === 'dark'
      ? 'bg-gray-950 text-white border border-gray-800 dark:bg-gray-800 dark:border-gray-700'
      : 'bg-white text-gray-700 border border-gray-200 dark:bg-[#1E2634] dark:text-white dark:border-gray-700';
  }

  get arrowBg(): string {
    return this.variant === 'dark'
      ? 'bg-gray-950 dark:bg-gray-800'
      : 'bg-white dark:bg-[#1E2634]';
  }

  get arrowBorderClasses(): string {
    return this.variant === 'dark'
      ? 'border-gray-800 dark:border-gray-700'
      : 'border-gray-200 dark:border-gray-700';
  }

  onMouseEnter() {
    clearTimeout(this.openTimeout);
    this.openTimeout = setTimeout(() => this.show(), 100);
  }

  onMouseLeave() {
    clearTimeout(this.openTimeout);
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  onFocus() {
    this.show();
  }

  onBlur() {
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  private async show() {
    if (!this.triggerRef?.nativeElement || !this.floatingRef?.nativeElement) return;

    const { x, y, placement, middlewareData } = await computePosition(
      this.triggerRef.nativeElement,
      this.floatingRef.nativeElement,
      {
        strategy: 'fixed',
        placement: this.placementMap[this.placement],
        middleware: [
          offset(10),
          flip({ fallbackAxisSideDirection: 'start' }),
          shift({ padding: 8 }),
          arrow({ element: this.arrowRef.nativeElement, padding: 8 }),
        ],
      }
    );

    this.floatingStyles = { top: y, left: x };

    const arrowX = middlewareData.arrow?.x;
    const arrowY = middlewareData.arrow?.y;
    const side = placement.split('-')[0];
    this.arrowStyles = this.getArrowStyles(side, arrowX, arrowY);
    this.arrowBorderSides = this.getArrowBorderSides(side);

    this.isOpen = true;
    this.cdr.markForCheck();
  }

  private getArrowStyles(side: string, arrowX?: number, arrowY?: number): any {
    switch (side) {
      case 'top':
        return { bottom: '-6px', left: arrowX != null ? `${arrowX}px` : '50%' };
      case 'bottom':
        return { top: '-6px', left: arrowX != null ? `${arrowX}px` : '50%' };
      case 'left':
        return { right: '-6px', top: arrowY != null ? `${arrowY}px` : '50%' };
      case 'right':
        return { left: '-6px', top: arrowY != null ? `${arrowY}px` : '50%' };
      default:
        return {};
    }
  }

  private getArrowBorderSides(side: string): string {
    switch (side) {
      case 'top': return 'border-r border-b';
      case 'bottom': return 'border-l border-t';
      case 'left': return 'border-r border-t';
      case 'right': return 'border-l border-b';
      default: return '';
    }
  }

  ngOnDestroy() {
    clearTimeout(this.openTimeout);
  }
}
